import { Sparkles, FileText, X } from 'lucide-react';
import type { AttachedContextFile } from '../../../../shared/types/brain';
import { useTranslation } from 'react-i18next';

interface AttachedSkill {
  name: string;
  prompt: string;
}

interface AttachmentBarProps {
  skill: AttachedSkill | null;
  contextFiles: AttachedContextFile[];
  onRemoveSkill: () => void;
  onRemoveFile: (key: string) => void;
}

export function AttachmentBar({ skill, contextFiles, onRemoveSkill, onRemoveFile }: AttachmentBarProps) {
  const { t } = useTranslation();
  if (!skill && contextFiles.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 px-4 py-2 border-t border-border bg-muted/30">
      {skill && (
        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
          <Sparkles className="w-3 h-3" />
          {skill.name}
          <button
            onClick={onRemoveSkill}
            className="ml-0.5 hover:text-primary/60 transition-colors"
            aria-label={t('brain.attachmentbar.remove_skill')}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      )}
      {contextFiles.map((f) => (
        <span
          key={f.key}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted border border-border text-muted-foreground"
        >
          <FileText className="w-3 h-3" />
          <span className="max-w-[120px] truncate">{f.filename}</span>
          {f.uploading ? (
            <svg className="w-3 h-3 animate-spin ml-0.5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
          ) : (
            <button
              onClick={() => onRemoveFile(f.key)}
              className="ml-0.5 hover:text-foreground transition-colors"
              aria-label={`Remove ${f.filename}`}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
}
